import { useNavigate } from 'react-router-dom';
import { Chip } from './Chips';
import { CardSkeleton, ErrorState } from './Feedback';
import { useAsync } from '../lib/hooks';
import { partnerConnections, partnerEntitlements } from '../lib/endpoints';
import { formatDateTime, formatRelative } from '../lib/format';

/**
 * TRADING PARTNER, ON THE SUPPLIER'S OWN CARD — mirrors the Business Central MultiEntity ToolKit's
 * "Initiate iERP AI Handshake" action on its own Vendor Card (Vendor Card Learning iER page
 * extension), which opens the Business Directory with the vendor pre-selected. This does the same
 * thing here: rather than re-implementing the directory search and connection-request flow inside
 * this drawer, it deep-links to the full Trading Partners page with this vendor's id and name
 * carried along, so Request Connection there arrives with the vendor already chosen instead of
 * making the operator search for it a second time.
 *
 * A vendor already bound to a connection shows that connection's status instead of the search
 * link — starting a second handshake for an already-connected vendor is never the right action.
 */
export function VendorTradingPartnerPanel({
  qboVendorId,
  vendorName,
}: {
  qboVendorId: string;
  vendorName: string | null;
}): JSX.Element {
  const navigate = useNavigate();
  const entitlement = useAsync((signal) => partnerEntitlements.get(signal), []);
  const connections = useAsync((signal) => partnerConnections.list(signal), []);

  if ((entitlement.loading && !entitlement.data) || (connections.loading && !connections.data)) {
    return <CardSkeleton />;
  }

  if (entitlement.error && !entitlement.data) {
    return <ErrorState message="Handshake entitlement could not be read." detail={entitlement.error} onRetry={entitlement.reload} />;
  }

  const bound = (connections.data ?? []).find((c) => c.boundQboVendorId === qboVendorId) ?? null;

  if (bound) {
    return (
      <div className="stack">
        <div className="banner banner-good" role="status">
          <div className="banner-body">
            <p className="banner-title">
              Connected to {bound.counterpartyCompanyName ?? 'this partner'} <Chip tone="good">{bound.status}</Chip>
            </p>
            <p className="banner-detail">
              {bound.relationshipRole} · {bound.isBuyer ? 'we are the buyer' : 'we are the seller'} · bound{' '}
              {bound.boundUtc ? formatRelative(bound.boundUtc) : 'recently'}
            </p>
          </div>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate('/partners/connections')}>
          Manage on Trading Partners
        </button>
      </div>
    );
  }

  const blocked = entitlement.data && !entitlement.data.startHandshake.enabled;

  if (blocked) {
    return (
      <div className="banner banner-neutral banner-inline">
        <div className="banner-body">
          <p className="banner-title">Starting a handshake is not available to this company</p>
          <p className="banner-detail">
            {entitlement.data?.startHandshake.message
              ?? 'iERP AI did not grant this company permission to start trading-partner handshakes.'}
          </p>
          <p className="banner-detail">
            Checked {entitlement.data ? formatDateTime(entitlement.data.startHandshake.checkedUtc) : '—'}.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <p className="field-hint">
        No trading-partner connection is bound to this vendor yet. Search the directory to find them
        and request one — they accept it on their side before anything can be exchanged.
      </p>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() =>
          navigate(
            `/partners/connections?vendorId=${encodeURIComponent(qboVendorId)}&vendorName=${encodeURIComponent(vendorName ?? '')}`,
          )
        }
      >
        Search the directory for {vendorName ?? 'this vendor'}
      </button>
    </div>
  );
}
